import { Search } from 'lucide-react';
import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
  Badge,
  type BadgeTone,
} from '@wundo/ui';
import type { KnowledgeBaseResult } from './knowledgeBase.ts';

const SOURCE_LABEL: Record<string, { text: string; tone: BadgeTone }> = {
  both: { text: 'Semantic + Text', tone: 'success' },
  vector: { text: 'Semantic', tone: 'info' },
  text: { text: 'Text Match', tone: 'neutral' },
};

/* How much the score is worth trusting, said in colour as well as in the number. */
function confidenceTone(similarity: number): BadgeTone {
  if (similarity >= 0.8) return 'success';
  if (similarity >= 0.65) return 'warning';
  return 'quiet';
}

export interface KnowledgeBaseSearchResultsProps {
  results: KnowledgeBaseResult[];
  query: string;
}

/**
 * What the agent found when it searched the knowledge base, and how sure it
 * was. Each chunk opens to its full text and its raw scores, because the
 * question this answers is usually "why did it pick that one".
 */
export function KnowledgeBaseSearchResults({
  results,
  query,
}: KnowledgeBaseSearchResultsProps) {
  return (
    <div className='flex flex-col gap-3'>
      <div className='flex items-start gap-2'>
        <Search className='mt-0.5 size-4 shrink-0 text-ink-muted' aria-hidden='true' />
        <div className='min-w-0 flex-1'>
          <p className='text-sm font-bold text-ink'>Knowledge Base Search</p>
          <p className='text-xs text-ink-muted'>{query}</p>
        </div>
        <Badge tone='quiet'>
          {results.length} result{results.length === 1 ? '' : 's'}
        </Badge>
      </div>

      {results.length === 0 ? (
        <p className='text-sm text-ink-muted italic'>No results found</p>
      ) : (
        <Accordion className='gap-2'>
          {results.map((result) => (
            <AccordionItem key={result.id}>
              <AccordionTrigger className='px-3.5 py-3'>
                <span className='flex min-w-0 flex-1 flex-col gap-1 text-left'>
                  <span className='truncate text-sm font-bold text-ink'>
                    {result.file_name}
                  </span>
                  <span className='line-clamp-2 text-xs font-normal text-ink-muted'>
                    {result.content}
                  </span>
                </span>
                <span className='flex shrink-0 items-center gap-2'>
                  {result.source && SOURCE_LABEL[result.source] ? (
                    <Badge tone={SOURCE_LABEL[result.source].tone}>
                      {SOURCE_LABEL[result.source].text}
                    </Badge>
                  ) : null}
                  {result.similarity !== undefined ? (
                    <Badge tone={confidenceTone(result.similarity)}>
                      {`${(result.similarity * 100).toFixed(0)}%`}
                    </Badge>
                  ) : null}
                </span>
              </AccordionTrigger>
              <AccordionPanel className='px-3.5 pb-3'>
                <p className='rounded-md bg-fill-faint p-3 text-xs break-words whitespace-pre-wrap text-ink-body'>
                  {result.content}
                </p>
                <p className='mt-2 flex flex-wrap gap-3 text-xs text-ink-muted'>
                  {result.similarity !== undefined && (
                    <span>Similarity: {result.similarity.toFixed(4)}</span>
                  )}
                  {result.text_rank !== undefined && (
                    <span>Text rank: {result.text_rank.toFixed(4)}</span>
                  )}
                  {result.source && <span>Source: {result.source}</span>}
                </p>
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
}
